export const CANONICAL_SCROLL_CONTINUATION_EVENT = 'aa:navigation-canonical-scroll-continuation';
export const CANONICAL_SCROLL_CONTINUATION_CANCEL_EVENT = 'aa:navigation-canonical-scroll-continuation-cancel';

const MAX_IDENTITY_PART_LENGTH = 4_096;
const MAX_LOCATION_PART_LENGTH = 65_536;

export interface CanonicalScrollContinuationRequest {
  scopeId: string;
  fromLocationKey: string;
  pathname: string;
  search: string;
  hash: string;
}

function validIdentityPart(value: unknown): value is string {
  return typeof value === 'string'
    && value.trim().length > 0
    && value.length <= MAX_IDENTITY_PART_LENGTH;
}

export function isCanonicalScrollContinuationRequest(
  value: unknown,
): value is CanonicalScrollContinuationRequest {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  let keys: PropertyKey[];
  try {
    keys = Reflect.ownKeys(value);
  } catch {
    return false;
  }
  const expected = ['scopeId', 'fromLocationKey', 'pathname', 'search', 'hash'];
  if (keys.length !== expected.length || expected.some((key) => !keys.includes(key))) return false;
  const own = (key: string): unknown => {
    try {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      return descriptor && 'value' in descriptor ? descriptor.value : undefined;
    } catch {
      return undefined;
    }
  };
  const pathname = own('pathname');
  const search = own('search');
  const hash = own('hash');
  return validIdentityPart(own('scopeId'))
    && validIdentityPart(own('fromLocationKey'))
    && typeof pathname === 'string'
    && pathname.startsWith('/')
    && pathname.length <= MAX_LOCATION_PART_LENGTH
    && typeof search === 'string'
    && (search === '' || search.startsWith('?'))
    && search.length <= MAX_LOCATION_PART_LENGTH
    && typeof hash === 'string'
    && (hash === '' || hash.startsWith('#'))
    && hash.length <= MAX_LOCATION_PART_LENGTH;
}

/**
 * Announces an automatic canonical REPLACE before it is issued. Keeping the
 * short-lived intent in the mounted scroll provider preserves location.state
 * byte-for-byte and prevents the intent leaking into later history entries.
 */
export function requestCanonicalScrollContinuation(
  request: CanonicalScrollContinuationRequest,
): () => void {
  if (!isCanonicalScrollContinuationRequest(request)) return () => undefined;
  window.dispatchEvent(new CustomEvent(CANONICAL_SCROLL_CONTINUATION_EVENT, {
    detail: request,
  }));
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    window.dispatchEvent(new CustomEvent(CANONICAL_SCROLL_CONTINUATION_CANCEL_EVENT, {
      detail: request,
    }));
  };
}

/**
 * Correlates a canonical intent with exactly one navigation attempt. A blocked,
 * reset, rejected, or otherwise no-op navigation settles the same intent on the
 * next task so it cannot leak into a later unrelated REPLACE.
 */
export function performCanonicalScrollContinuation(
  request: CanonicalScrollContinuationRequest,
  navigate: () => void | Promise<void>,
): void {
  const settle = requestCanonicalScrollContinuation(request);
  const settleAfterRouterCommit = () => window.setTimeout(settle, 0);
  try {
    const result = navigate();
    if (result && typeof result.then === 'function') {
      void Promise.resolve(result).then(settleAfterRouterCommit, settleAfterRouterCommit);
    } else {
      settleAfterRouterCommit();
    }
  } catch (error) {
    settle();
    throw error;
  }
}
